import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ResponsiveTable } from '@/components/responsive-table';
import { ListPagination, ScrollArea } from '@/components/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useVotes } from '@/lib/api-hooks';
import { VOTE_PAGE_SIZE_OPTIONS, type Vote } from '@/lib/votes';
import { useVotesStore } from '@/lib/votes-store';

export function VotesPage() {
  const search = useVotesStore((state) => state.search);
  const page = useVotesStore((state) => state.page);
  const pageSize = useVotesStore((state) => state.pageSize);
  const setSearch = useVotesStore((state) => state.setSearch);
  const setPage = useVotesStore((state) => state.setPage);
  const setPageSize = useVotesStore((state) => state.setPageSize);

  const { data, isLoading } = useVotes({ search, page, pageSize });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-60">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search votes"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <ScrollArea>
        <ResponsiveTable
          loading={isLoading}
          loadingLabel="Loading votes…"
          emptyLabel="No votes match your search."
          skeletonRows={pageSize}
          rows={items}
          rowKey={(vote) => vote.id}
          columns={[
            {
              key: 'project',
              header: 'Project',
              title: true,
              cell: (vote: Vote) => (
                <Link to={`/projects/${vote.projectSlug}`} className="font-medium hover:underline">
                  {vote.project}
                </Link>
              ),
            },
            {
              key: 'proposal',
              header: 'Proposal',
              cell: (vote: Vote) => (
                <Link to={`/proposals/${vote.proposalSlug}`} className="hover:underline">
                  {vote.proposal}
                </Link>
              ),
            },
            {
              key: 'artist',
              header: 'Artist',
              secondary: true,
              cell: (vote: Vote) => (
                <Link
                  to={`/artists/${vote.artistSlug}`}
                  className="text-muted-foreground hover:text-foreground hover:underline"
                >
                  {vote.artist}
                </Link>
              ),
            },
            {
              key: 'visitor',
              header: 'Visitor',
              secondary: true,
              cell: (vote: Vote) => (
                <span className="font-mono text-muted-foreground text-xs">{vote.visitorId}</span>
              ),
            },
            {
              key: 'date',
              header: 'Date',
              align: 'right',
              cell: (vote: Vote) => <span className="text-muted-foreground">{vote.date}</span>,
            },
          ]}
        />

        {(total > 0 || isLoading) && (
          <ListPagination>
            <p className="text-muted-foreground text-sm">
              {isLoading ? 'Loading…' : `Showing ${start}–${end} of ${total}`}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Rows per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => setPageSize(Number(value))}
                >
                  <SelectTrigger size="sm" className="w-[4.5rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VOTE_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isLoading}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => (
                  <Button
                    key={number}
                    variant={number === page ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage(number)}
                    disabled={isLoading}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </ListPagination>
        )}
      </ScrollArea>
    </div>
  );
}
